import { useAuth } from "@/composables/useAuth.js";
import router from "@/router";
import ModalManager from "@/components/modal/ModalManager";
import ModalConfirm from "@/components/modal/ModalConfirm.vue";

import TypeGroupEditor from "@/components/editors/TypeGroupEditor.vue";
import ArticleTypesEditor from "@/components/editors/ArticleTypesEditor.vue";
import ArticleMasterEditor from "@/components/editors/ArticleMasterEditor.vue";
import ArticleDuplicateEditor from "@/components/editors/ArticleDuplicateEditor.vue";
import StockTypesEditor from "@/components/editors/StockTypesEditor.vue";
import BricksEditor from "@/components/editors/BricksEditor.vue";

export function useModalOpeners() {

  async function openEditor({
    title,
    component,
    props = {},
    record = null,
    size = "md",
    detectValidation = null,
    onClose = null
  }) {

    const auth = useAuth();   // REAL shared instance

    // Rehydrate token from localStorage
    if (!auth.accessToken.value) {
      const stored = localStorage.getItem("access_token");
      if (stored) auth.accessToken.value = stored;
    }

    // No token = redirect
    if (!auth.accessToken.value) {
      router.push("/login");
      return;
    }

    // Ensure user loaded
    if (!auth.isAuthenticated()) {
      const ok = await auth.fetchUser();
      if (!ok) {
        router.push("/login");
        return;
      }
    }

    // Open editor
    ModalManager.open({
      title,
      component,
      props,
      record,
      size,
      detectValidation,
      onClose
    });
  }

  function openTypeGroupEditor(article_type, onClose) {
    openEditor({
      title: "Edit Type Group",
      component: TypeGroupEditor,
      props: { article_type },
      record: article_type,
      size: "sm",
      detectValidation: () => {
        const inst = ModalManager.state.instance;
        return Array.isArray(inst?.assignedGroupIds) &&
               inst.assignedGroupIds.length > 0;
      },
      onClose
    });
  }

  function openArticleTypesEditor(article, onClose) {
    openEditor({
      title: "Edit Types",
      component: ArticleTypesEditor,
      props: { article },
      record: article,
      size: "lg",
      detectValidation: () => {
        const inst = ModalManager.state.instance;
        return Array.isArray(inst?.selected) &&
               inst.selected.length > 0;
      },
      onClose
    });
  }

  function openArticleMasterEditor(article, onClose) {
      openEditor({
          title: "Edit Master Mapping",
          component: ArticleMasterEditor,     // your Vue component
          props: { article },                 // pass article object
          record: article,                    // same pattern as TypesEditor
          size: "lg",
          detectValidation: () => {
              const inst = ModalManager.state.instance;

              // The master editor typically returns:
              // inst.selectedMaster   -> null or article object
              // inst.children         -> array when master

              // Accept either:
              // 1. a valid selected master (when article is child)
              // 2. no selection required if article is a master (children mode)

              if (!inst) return false;

              // Case A: Child article → master must be selected or cleared
              if (inst.isMaster === false) {
                  return (
                      inst.selectedMaster !== undefined // allows null (remove mapping)
                  );
              }

              // Case B: Master article → children is always valid
              if (inst.isMaster === true) {
                  return true;
              }

              return false;
          },
          onClose
      });
  }

  function openArticleDuplicateEditor(article, onClose) {
    openEditor({
      title: "Duplicate Article",
      component: ArticleDuplicateEditor,
      props: { article },
      record: article,
      size: "md",

      detectValidation: () => {
        const inst = ModalManager.state.instance;

        if (!inst) return false;

        // Block closing while duplication is in progress
        return inst.working === false;
      },

      onClose
    });
  }

  function openStocktypeEditor(article, onClose) {
    openEditor({
      title: "Variant Stock Editor",
      component: StockTypesEditor,
      props: { article },
      record: article,
      size: "lg",
      detectValidation: () => {
        const inst = ModalManager.state.instance;
        return inst?.variants?.some(v =>
          Number(v.curstock) > 0 ||
          Number(v.upprice) !== 0 ||
          (v.sku_thirdparty?.trim?.() || "") !== ""
        );
      },
      onClose
    });
  }

  function openBricksEditor(article, onClose) {
    openEditor({
      title: "Edit Bricks",
      component: BricksEditor,
      props: { article },
      record: article,
      size: "xl",
      detectValidation: () => {
        const inst = ModalManager.state.instance;
        return Array.isArray(inst?.selected) &&
               inst.selected.length > 0;
      },
      onClose
    });
  }

  function openPDF(record) {
    if (!record?.id) return;
    const url = `/api/orders/pdf/${record.id}`;
    window.open(url, "_blank", "noopener");

  }  

  function openRemoveConfirm(record, onClose) {
    openEditor({
      title: "Remove",
      component: ModalConfirm,

      props: {
        message: "This action cannot be undone.",
        confirmLabel: "Remove",
        cancelLabel: "Cancel"
      },

      record,
      size: "sm",

      detectValidation: () => {
        const inst = ModalManager.state.instance;
        if (!inst) return false;
        return inst.working === false;
      },

      onClose: confirmed => {
        if (confirmed === true) {
          onClose?.(record);
        }
        // else: cancelled → do nothing

      }
    });
  }


  return {
    openEditor,
    openTypeGroupEditor,
    openArticleTypesEditor,
    openArticleMasterEditor,
    openArticleDuplicateEditor,
    openStocktypeEditor,
    openBricksEditor,
    openPDF,
    openRemoveConfirm
  };
}
